import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Single source of truth for Pindi Inc company + payment details.
// Used by both the on-screen invoice card AND the printable version so
// the numbers never fall out of sync.
const COMPANY = {
  name: 'Pindi Inc',
  tagline: 'Software Solutions',
  address: '233 Bay 34th Street, Apt 3B',
  phone: '347-981-2741',
  email: 'shabazbaig786@gmail.com',
};

const PAYMENT = {
  bank: 'Chase Bank',
  beneficiary: 'Pindi Inc',
  accountNumber: '978764298',
  routingNumber: '021000021',
};

const Bills = ({ embedded = false }) => {
  const navigate = useNavigate();
  const invoiceRef = useRef(null);

  const now = new Date();
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // Billing began in January 2025 — generate every month up to the current month
  const BILLING_START = { year: 2025, month: 0 }; // 0-indexed month → January
  const allMonths = React.useMemo(() => {
    const list = [];
    let y = BILLING_START.year;
    let m = BILLING_START.month;
    while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
      list.push({ year: y, month: m });
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
    return list.reverse(); // newest first
  }, []);

  // Default selection = current month (index 0 since we reversed)
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  const selected = allMonths[selectedIdx] || { year: now.getFullYear(), month: now.getMonth() };

  const currentMonth = monthNames[selected.month];
  const currentYear = selected.year;
  const invoiceNumber = `ASH-${currentYear}${String(selected.month + 1).padStart(2, '0')}`;
  const dueDate = new Date(currentYear, selected.month + 1, 0);
  const invoiceDate = new Date(currentYear, selected.month, 1);
  const isCurrent = selected.year === now.getFullYear() && selected.month === now.getMonth();
  const isPast = new Date(currentYear, selected.month + 1, 0) < new Date(now.getFullYear(), now.getMonth(), 1);

  const lineItems = [
    {
      description: 'Emergent Platform - Monthly Subscription',
      details: 'Full-stack hosting, AI tools, deployment pipeline',
      amount: 200.00
    },
    {
      description: 'Live App Hosting',
      details: 'Production deployment & CDN (50 credits/month)',
      amount: 10.00
    },
    {
      description: 'Microsoft Azure - Computer Vision',
      details: 'OCR package label scanning service',
      amount: 29.99
    }
  ];

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const tax = 0;
  const total = subtotal + tax;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the invoice.');
      return;
    }
    const rowsHtml = lineItems.map((item, index) => `
      <tr>
        <td class="col-num">${index + 1}</td>
        <td class="col-desc">
          <div class="item-title">${item.description}</div>
          <div class="item-details">${item.details}</div>
        </td>
        <td class="col-amount">$${item.amount.toFixed(2)}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoice ${invoiceNumber} - ${COMPANY.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 32px; color: #1f2937; background: #fff; }
            .invoice { max-width: 820px; margin: 0 auto; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
            .header { background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%); color: #fff; padding: 28px 32px; display: flex; justify-content: space-between; align-items: flex-start; }
            .company-name { font-size: 26px; font-weight: 700; margin-bottom: 4px; }
            .company-sub { font-size: 13px; color: #e0e7ff; margin-bottom: 8px; }
            .company-line { font-size: 12px; color: #e0e7ff; line-height: 1.6; }
            .meta { text-align: right; }
            .meta-title { font-size: 30px; font-weight: 700; letter-spacing: 3px; margin-bottom: 8px; }
            .meta-line { font-size: 12px; color: #e0e7ff; line-height: 1.7; }
            .bill-to { margin: 24px 32px; background: #f8fafc; border-radius: 10px; padding: 18px 20px; }
            .bill-to-label { font-size: 11px; color: #9ca3af; text-transform: uppercase; letter-spacing: 1.2px; margin-bottom: 6px; font-weight: 600; }
            .bill-to-name { font-size: 17px; font-weight: 700; color: #111827; margin-bottom: 2px; }
            .bill-to-line { font-size: 13px; color: #6b7280; line-height: 1.5; }
            table.items { width: calc(100% - 64px); margin: 0 32px 24px; border-collapse: collapse; }
            table.items thead th { background: #4f46e5; color: #fff; padding: 12px 14px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
            table.items thead th:first-child { border-top-left-radius: 8px; width: 40px; }
            table.items thead th:last-child { text-align: right; border-top-right-radius: 8px; }
            table.items tbody td { padding: 14px; border-bottom: 1px solid #e5e7eb; font-size: 14px; vertical-align: top; }
            table.items tbody td.col-num { color: #9ca3af; font-weight: 500; width: 40px; }
            table.items tbody td.col-amount { text-align: right; font-weight: 700; color: #111827; font-size: 15px; white-space: nowrap; }
            .item-title { font-weight: 600; color: #111827; margin-bottom: 3px; }
            .item-details { font-size: 12px; color: #6b7280; }
            .totals-wrap { display: flex; justify-content: flex-end; margin: 0 32px 24px; }
            .totals { width: 280px; }
            .totals .row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; color: #4b5563; }
            .totals .row strong { color: #111827; font-weight: 600; }
            .totals .row.grand { border-top: 2px solid #4f46e5; margin-top: 6px; padding-top: 12px; }
            .totals .row.grand .label, .totals .row.grand .value { font-size: 18px; font-weight: 700; color: #4f46e5; }
            .pay { margin: 0 32px 24px; background: linear-gradient(135deg, #eef2ff 0%, #eff6ff 100%); border: 2px solid #c7d2fe; border-radius: 10px; padding: 16px 18px; }
            .pay-title { display: flex; align-items: center; gap: 8px; font-size: 11px; text-transform: uppercase; letter-spacing: 1.2px; color: #4f46e5; font-weight: 700; margin-bottom: 10px; }
            .pay-title-dot { width: 24px; height: 24px; border-radius: 6px; background: #4f46e5; color: #fff; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
            .pay-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
            .pay-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; color: #9ca3af; font-weight: 600; margin-bottom: 2px; }
            .pay-value { font-weight: 700; color: #111827; font-size: 14px; }
            .pay-value.mono { font-family: 'Menlo', 'Consolas', monospace; letter-spacing: 0.5px; }
            .stamp-wrap { text-align: center; padding: 0 32px 20px; }
            .stamp { display: inline-block; padding: 6px 20px; border: 3px solid #10b981; color: #10b981; font-size: 18px; font-weight: 800; text-transform: uppercase; letter-spacing: 3px; border-radius: 8px; transform: rotate(-2deg); }
            .footer { background: #f9fafb; padding: 14px 32px; text-align: center; border-top: 1px solid #e5e7eb; }
            .footer p { font-size: 12px; color: #9ca3af; margin: 2px 0; }
            @media print {
              body { padding: 0; }
              .invoice { border: none; border-radius: 0; }
            }
          </style>
        </head>
        <body>
          <div class="invoice">
            <div class="header">
              <div>
                <div class="company-name">${COMPANY.name}</div>
                <div class="company-sub">${COMPANY.tagline}</div>
                <div class="company-line">${COMPANY.address}</div>
                <div class="company-line">Tel: ${COMPANY.phone}</div>
                <div class="company-line">${COMPANY.email}</div>
              </div>
              <div class="meta">
                <div class="meta-title">INVOICE</div>
                <div class="meta-line">#${invoiceNumber}</div>
                <div class="meta-line">Date: ${currentMonth} 1, ${currentYear}</div>
                <div class="meta-line">Due: ${monthNames[dueDate.getMonth()]} ${dueDate.getDate()}, ${currentYear}</div>
              </div>
            </div>

            <div class="bill-to">
              <div class="bill-to-label">Bill To</div>
              <div class="bill-to-name">Ashop Rotterdam</div>
              <div class="bill-to-line">Package Management Services</div>
              <div class="bill-to-line">Rotterdam, Netherlands</div>
              <div class="bill-to-line">Billing Period: ${currentMonth} ${currentYear}</div>
            </div>

            <table class="items">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>

            <div class="totals-wrap">
              <div class="totals">
                <div class="row"><span>Subtotal</span><strong>$${subtotal.toFixed(2)}</strong></div>
                <div class="row"><span>Tax</span><strong>$${tax.toFixed(2)}</strong></div>
                <div class="row grand"><span class="label">Total</span><span class="value">$${total.toFixed(2)}</span></div>
              </div>
            </div>

            <div class="pay">
              <div class="pay-title"><span class="pay-title-dot">$</span> Payment Details</div>
              <div class="pay-grid">
                <div>
                  <div class="pay-label">Bank</div>
                  <div class="pay-value">${PAYMENT.bank}</div>
                </div>
                <div>
                  <div class="pay-label">Beneficiary</div>
                  <div class="pay-value">${PAYMENT.beneficiary}</div>
                </div>
                <div>
                  <div class="pay-label">Account Number</div>
                  <div class="pay-value mono">${PAYMENT.accountNumber}</div>
                </div>
                <div>
                  <div class="pay-label">Routing Number (ABA)</div>
                  <div class="pay-value mono">${PAYMENT.routingNumber}</div>
                </div>
              </div>
            </div>

            <div class="stamp-wrap">
              <span class="stamp" style="border-color:${isPast ? '#10b981' : isCurrent ? '#f59e0b' : '#6366f1'};color:${isPast ? '#10b981' : isCurrent ? '#f59e0b' : '#6366f1'};">${isPast ? 'Paid' : isCurrent ? 'Due This Month' : 'Upcoming'}</span>
            </div>

            <div class="footer">
              <p>Thank you for your business!</p>
              <p>${COMPANY.name} &mdash; ${COMPANY.tagline}</p>
            </div>
          </div>
          <script>
            window.addEventListener('load', function() {
              setTimeout(function() { window.focus(); window.print(); }, 300);
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4'}>
      <div className={embedded ? '' : 'max-w-4xl mx-auto'}>
        {/* Back button & Actions */}
        <div className="flex justify-between items-center mb-6">
          {!embedded ? (
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center space-x-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200"
              data-testid="back-to-admin-btn"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Back to Admin</span>
            </button>
          ) : <div />}
          <button
            onClick={handlePrint}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="print-invoice-btn"
          >
            <i className="fas fa-print"></i>
            <span>Print Invoice</span>
          </button>
        </div>

        {/* Month selector — every invoice from Jan 2025 to today */}
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-3 mb-5" data-testid="bills-month-selector">
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <i className="fas fa-clock-rotate-left mr-1"></i>
              All invoices — {allMonths.length} months
            </p>
            {!isCurrent && (
              <button
                onClick={() => setSelectedIdx(0)}
                className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 hover:text-emerald-700"
                data-testid="jump-to-current-btn"
              >
                Jump to current
              </button>
            )}
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            {allMonths.map((m, i) => {
              const isSel = i === selectedIdx;
              const isThisMonth = m.year === now.getFullYear() && m.month === now.getMonth();
              return (
                <button
                  key={`${m.year}-${m.month}`}
                  onClick={() => setSelectedIdx(i)}
                  className={`shrink-0 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                    isSel
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}
                  data-testid={`month-chip-${m.year}-${m.month + 1}`}
                >
                  <span className="block leading-none">{monthNames[m.month].slice(0, 3)}</span>
                  <span className={`block text-[9px] leading-tight mt-0.5 ${isSel ? 'text-indigo-100' : 'text-slate-400'}`}>
                    {m.year}
                    {isThisMonth ? ' · Now' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Invoice Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden" ref={invoiceRef}>
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-8 py-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-white">{COMPANY.name}</h1>
                <p className="text-indigo-200 text-sm mt-1">{COMPANY.tagline}</p>
                <p className="text-indigo-200 text-sm">{COMPANY.address}</p>
                <p className="text-indigo-200 text-sm">Tel: {COMPANY.phone}</p>
                <p className="text-indigo-200 text-sm">{COMPANY.email}</p>
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-bold text-white tracking-wider">INVOICE</h2>
                <p className="text-indigo-200 text-sm mt-2">#{invoiceNumber}</p>
                <p className="text-indigo-200 text-sm">Date: {currentMonth} 1, {currentYear}</p>
                <p className="text-indigo-200 text-sm">Due: {monthNames[dueDate.getMonth()]} {dueDate.getDate()}, {currentYear}</p>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="px-8 pt-6">
            <div className="bg-gray-50 rounded-xl p-5 mb-6">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">Bill To</p>
              <p className="font-bold text-gray-800 text-lg">Ashop Rotterdam</p>
              <p className="text-gray-600 text-sm">Package Management Services</p>
              <p className="text-gray-500 text-sm mt-1">Rotterdam, Netherlands</p>
              <p className="text-gray-500 text-sm">Billing Period: {currentMonth} {currentYear}</p>
            </div>
          </div>

          {/* Line Items */}
          <div className="px-8">
            <table className="w-full">
              <thead>
                <tr className="bg-indigo-600">
                  <th className="text-left py-4 px-4 text-white text-sm font-semibold uppercase tracking-wider rounded-tl-lg">#</th>
                  <th className="text-left py-4 px-4 text-white text-sm font-semibold uppercase tracking-wider">Description</th>
                  <th className="text-right py-4 px-4 text-white text-sm font-semibold uppercase tracking-wider rounded-tr-lg">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`invoice-line-${index}`}>
                    <td className="py-5 px-4 text-gray-400 font-medium">{index + 1}</td>
                    <td className="py-5 px-4">
                      <p className="font-semibold text-gray-800">{item.description}</p>
                      <p className="text-gray-400 text-sm mt-1">{item.details}</p>
                    </td>
                    <td className="py-5 px-4 text-right font-bold text-gray-800 text-lg">${item.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="px-8 py-6">
            <div className="flex justify-end">
              <div className="w-72">
                <div className="flex justify-between py-2 text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 text-gray-600">
                  <span>Tax</span>
                  <span className="font-semibold">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 mt-2 border-t-2 border-indigo-600">
                  <span className="text-xl font-bold text-indigo-600">Total</span>
                  <span className="text-xl font-bold text-indigo-600" data-testid="invoice-total">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="px-8 pb-6" data-testid="payment-details">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="fas fa-university text-white text-sm"></i>
                </div>
                <p className="text-xs uppercase tracking-widest text-indigo-600 font-bold">Payment Details</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Bank</p>
                  <p className="font-bold text-gray-800">{PAYMENT.bank}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Beneficiary</p>
                  <p className="font-bold text-gray-800">{PAYMENT.beneficiary}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Account Number</p>
                  <p className="font-mono font-bold text-gray-800 tracking-wide" data-testid="account-number">{PAYMENT.accountNumber}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Routing Number (ABA)</p>
                  <p className="font-mono font-bold text-gray-800 tracking-wide" data-testid="routing-number">{PAYMENT.routingNumber}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="px-8 pb-6 text-center">
            <span
              className="inline-block px-6 py-2 font-extrabold text-xl uppercase tracking-widest rounded-lg"
              style={{
                border: `3px solid ${isPast ? '#10B981' : isCurrent ? '#f59e0b' : '#6366f1'}`,
                color: isPast ? '#10B981' : isCurrent ? '#f59e0b' : '#6366f1',
                transform: 'rotate(-2deg)',
                display: 'inline-block'
              }}
              data-testid="invoice-status-stamp"
            >
              {isPast ? 'Paid' : isCurrent ? 'Due This Month' : 'Upcoming'}
            </span>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 text-center border-t border-gray-200">
            <p className="text-gray-400 text-sm">Thank you for your business!</p>
            <p className="text-gray-400 text-xs mt-1">{COMPANY.name} - {COMPANY.tagline}</p>
          </div>
        </div>

        {/* Monthly Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100" data-testid="monthly-cost-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-server text-indigo-600"></i>
              </div>
              <p className="text-gray-500 text-sm font-medium">Emergent Platform</p>
            </div>
            <p className="text-3xl font-bold text-gray-800">$200<span className="text-sm text-gray-400 font-normal">/mo</span></p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100" data-testid="hosting-cost-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-globe text-emerald-600"></i>
              </div>
              <p className="text-gray-500 text-sm font-medium">Live App Hosting</p>
            </div>
            <p className="text-3xl font-bold text-gray-800">$10<span className="text-sm text-gray-400 font-normal">/mo</span></p>
            <p className="text-xs text-gray-400 mt-1">50 credits/month</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100" data-testid="azure-cost-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="fab fa-microsoft text-blue-600"></i>
              </div>
              <p className="text-gray-500 text-sm font-medium">Microsoft Azure</p>
            </div>
            <p className="text-3xl font-bold text-gray-800">$29.99<span className="text-sm text-gray-400 font-normal">/mo</span></p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100" data-testid="total-cost-card">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-calculator text-purple-600"></i>
              </div>
              <p className="text-gray-500 text-sm font-medium">Total Monthly</p>
            </div>
            <p className="text-3xl font-bold text-indigo-600">$239.99<span className="text-sm text-gray-400 font-normal">/mo</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bills;
